import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { repairCache, stockCache, userCache } from "@/utils/cacheManager";
import { usePageVisibility } from "@/hooks/usePageVisibility";
import { Trash2, RefreshCw, Eye, EyeOff } from "lucide-react";

/**
 * Development component to monitor and manage cache status
 * Shows cache statistics and provides controls for testing
 */
export const CacheStatus: React.FC = () => {
  console.log("CacheStatus component is rendering!");

  const [repairStats, setRepairStats] = useState(repairCache.getStats());
  const [stockStats, setStockStats] = useState(stockCache.getStats());
  const [userStats, setUserStats] = useState(userCache.getStats());
  const { isVisible, lastVisibleTime } = usePageVisibility();
  const [refreshKey, setRefreshKey] = useState(0);

  // Update stats every 2 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setRepairStats(repairCache.getStats());
      setStockStats(stockCache.getStats());
      setUserStats(userCache.getStats());
      setRefreshKey((prev) => prev + 1);
    }, 2000);

    return () => clearInterval(interval);
  }, []);

  const clearAllCaches = () => {
    repairCache.clear();
    stockCache.clear();
    userCache.clear();
    setRefreshKey((prev) => prev + 1);
  };

  const clearRepairCache = () => {
    repairCache.clear();
    setRefreshKey((prev) => prev + 1);
  };

  const clearStockCache = () => {
    stockCache.clear();
    setRefreshKey((prev) => prev + 1);
  };

  const formatTime = (timestamp: number) => {
    const seconds = Math.floor((Date.now() - timestamp) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ago`;
  };

  return (
    <Card className="w-full max-w-2xl">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          Cache Status Monitor
          <Badge variant={isVisible ? "default" : "secondary"}>
            {isVisible ? (
              <Eye className="w-3 h-3" />
            ) : (
              <EyeOff className="w-3 h-3" />
            )}
            {isVisible ? "Visible" : "Hidden"}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Page Visibility Info */}
        <div className="p-3 bg-muted rounded-lg">
          <div className="text-sm font-medium">Page Visibility</div>
          <div className="text-xs text-muted-foreground">
            Last visible: {formatTime(lastVisibleTime)}
          </div>
        </div>

        {/* Cache Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-3 border rounded-lg">
            <div className="font-medium text-sm">Repair Cache</div>
            <div className="text-xs text-muted-foreground">
              Items: {repairStats.memoryItems}/{repairStats.maxMemoryItems}
            </div>
            <div className="text-xs text-muted-foreground">
              TTL: {Math.floor(repairStats.defaultTTL / 60000)}m
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={clearRepairCache}
              className="mt-2 w-full"
            >
              <Trash2 className="w-3 h-3 mr-1" />
              Clear
            </Button>
          </div>

          <div className="p-3 border rounded-lg">
            <div className="font-medium text-sm">Stock Cache</div>
            <div className="text-xs text-muted-foreground">
              Items: {stockStats.memoryItems}/{stockStats.maxMemoryItems}
            </div>
            <div className="text-xs text-muted-foreground">
              TTL: {Math.floor(stockStats.defaultTTL / 60000)}m
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={clearStockCache}
              className="mt-2 w-full"
            >
              <Trash2 className="w-3 h-3 mr-1" />
              Clear
            </Button>
          </div>

          <div className="p-3 border rounded-lg">
            <div className="font-medium text-sm">User Cache</div>
            <div className="text-xs text-muted-foreground">
              Items: {userStats.memoryItems}/{userStats.maxMemoryItems}
            </div>
            <div className="text-xs text-muted-foreground">
              TTL: {Math.floor(userStats.defaultTTL / 60000)}m
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => userCache.clear()}
              className="mt-2 w-full"
            >
              <Trash2 className="w-3 h-3 mr-1" />
              Clear
            </Button>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="destructive"
            onClick={clearAllCaches}
            className="flex-1"
          >
            <Trash2 className="w-3 h-3 mr-1" />
            Clear All Caches
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setRefreshKey((prev) => prev + 1)}
          >
            <RefreshCw className="w-3 h-3" />
          </Button>
        </div>

        {/* Instructions */}
        <div className="text-xs text-muted-foreground p-3 bg-muted rounded-lg">
          <strong>Testing Instructions:</strong>
          <br />
          1. Navigate to different tabs/pages to populate cache
          <br />
          2. Switch to another browser tab and come back
          <br />
          3. Notice faster loading times on subsequent visits
          <br />
          4. Use "Clear" buttons to test cache invalidation
        </div>
      </CardContent>
    </Card>
  );
};
